
/* Wambolt preorder PDP: sticky purchase panel and optional extra storage. */
(() => {
  'use strict';

  const registryKey = '__wamboltPreorderPdp';

  if (window[registryKey]) {
    window[registryKey].init();
    return;
  }

  const detailsSelector =
    '.wambolt-product-information:has(.wambolt-buy-cta) .product-details.sticky-content--desktop';

  const addonSelector = '[data-wambolt-extra-storage]';
  const detailsInstances = new Map();
  const busyForms = new WeakSet();

  /* -----------------------------------------
     STICKY PURCHASE PANEL
     ----------------------------------------- */

  function updateDetailsHeight(element) {
    if (!element.isConnected) return;

    const height =
      `${Math.ceil(element.getBoundingClientRect().height)}px`;

    if (
      element.style.getPropertyValue(
        '--wambolt-pdp-details-height'
      ) !== height
    ) {
      element.style.setProperty(
        '--wambolt-pdp-details-height',
        height
      );
    }
  }

  function disposeDetails(element) {
    const instance = detailsInstances.get(element);
    if (!instance) return;

    instance.observer?.disconnect();

    element.style.removeProperty(
      '--wambolt-pdp-details-height'
    );

    detailsInstances.delete(element);
  }

  function disposeAllDetails() {
    for (const element of Array.from(detailsInstances.keys())) {
      disposeDetails(element);
    }
  }

  function initDetails() {
    for (const element of Array.from(detailsInstances.keys())) {
      if (!element.isConnected) {
        disposeDetails(element);
      }
    }

    document.querySelectorAll(detailsSelector).forEach((element) => {
      if (detailsInstances.has(element)) {
        updateDetailsHeight(element);
        return;
      }

      let observer = null;

      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => {
          updateDetailsHeight(element);
        });

        observer.observe(element);

        const content =
          element.querySelector('.group-block-content');

        if (content) {
          observer.observe(content);
        }
      }

      detailsInstances.set(element, { observer });
      updateDetailsHeight(element);
    });
  }

  /* -----------------------------------------
     ADD-ON QUANTITY SELECTORS
     ----------------------------------------- */

  function numberOr(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function quantityLimits(input) {
    const min = Math.max(
      1,
      numberOr(input.dataset.min, 1)
    );

    const step = Math.max(
      1,
      numberOr(input.dataset.step, 1)
    );

    const rawMax = numberOr(
      input.dataset.max,
      Infinity
    );

    const max = Number.isFinite(rawMax)
      ? min + Math.floor((rawMax - min) / step) * step
      : Infinity;

    return { min, step, max };
  }

  function normalizeQuantity(input) {
    if (input.disabled) return 0;

    const { min, step, max } = quantityLimits(input);

    if (max < min) return 0;

    let quantity = numberOr(input.value, 0);

    if (quantity <= 0) return 0;

    if (quantity < min) {
      quantity = min;
    }

    quantity =
      min + Math.floor((quantity - min) / step) * step;

    return Math.min(quantity, max);
  }

  function syncRow(row) {
    const input = row.querySelector(
      '[data-wambolt-addon-quantity]'
    );

    if (!input) return;

    const quantity = normalizeQuantity(input);
    input.value = String(quantity);

    row.dataset.wamboltAddonSelected =
      quantity > 0 ? 'true' : 'false';

    const { min, step, max } = quantityLimits(input);

    const decrease = row.querySelector(
      '[data-wambolt-addon-action="decrease"]'
    );

    const increase = row.querySelector(
      '[data-wambolt-addon-action="increase"]'
    );

    if (decrease) {
      decrease.disabled =
        input.disabled || quantity === 0;
    }

    if (increase) {
      const next =
        quantity === 0 ? min : quantity + step;

      increase.disabled =
        input.disabled || next > max;
    }
  }

  function initAddons() {
    document
      .querySelectorAll(
        `${addonSelector} [data-wambolt-addon-item]`
      )
      .forEach(syncRow);
  }

  function onQuantityClick(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(
      '[data-wambolt-addon-action]'
    );

    if (!button || button.disabled) return;

    const row = button.closest(
      '[data-wambolt-addon-item]'
    );

    const input = row?.querySelector(
      '[data-wambolt-addon-quantity]'
    );

    if (!input || input.disabled) return;

    const current = normalizeQuantity(input);
    const { min, step, max } = quantityLimits(input);

    const next =
      button.dataset.wamboltAddonAction === 'increase'
        ? current === 0
          ? min
          : Math.min(max, current + step)
        : current <= min
          ? 0
          : current - step;

    input.value = String(Math.max(0, next));

    syncRow(row);

    input.dispatchEvent(
      new Event('change', { bubbles: true })
    );
  }

  function onQuantityChange(event) {
    if (!(event.target instanceof Element)) return;

    const input = event.target.closest(
      '[data-wambolt-addon-quantity]'
    );

    if (!input) return;

    const row = input.closest(
      '[data-wambolt-addon-item]'
    );

    if (row) {
      syncRow(row);
    }
  }

  function selectedAddons(addonRoot) {
    const result = [];

    for (
      const row of addonRoot.querySelectorAll(
        '[data-wambolt-addon-item]'
      )
    ) {
      const input = row.querySelector(
        '[data-wambolt-addon-quantity]'
      );

      if (!input) continue;

      const quantity = normalizeQuantity(input);
      input.value = String(quantity);

      if (quantity === 0) continue;

      const id = String(row.dataset.variantId || '');

      if (!/^\d+$/.test(id)) {
        throw new Error(
          'A storage product is not configured correctly.'
        );
      }

      result.push({
        id: Number(id),
        quantity
      });
    }

    return result;
  }

  function showStatus(root, message, isError) {
    const status = root.querySelector(
      '[data-wambolt-addon-status]'
    );

    if (!status) return;

    status.hidden = false;
    status.textContent = message;

    status.classList.toggle(
      'is-error',
      Boolean(isError)
    );
  }

  /* -----------------------------------------
     PREORDER: MAIN ITEM + SELECTED ADD-ONS
     ----------------------------------------- */

  async function submitWithAddons(
    form,
    productComponent,
    addonRoot,
    addOnItems
  ) {
    if (busyForms.has(form)) return;

    busyForms.add(form);

    const submitButton = form.querySelector(
      'button[type="submit"][name="add"]'
    );

    const formData = new FormData(form);

    const variantId = String(
      formData.get('id') || ''
    );

    const quantity = numberOr(
      formData.get('quantity') ||
        productComponent.dataset.quantityDefault,
      1
    );

    if (!/^\d+$/.test(variantId) || quantity < 1) {
      showStatus(
        addonRoot,
        'Please choose a valid product quantity.',
        true
      );

      busyForms.delete(form);
      return;
    }

    const cartItem = {
      id: Number(variantId),
      quantity
    };

    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);

      if (match) {
        properties[match[1]] = String(value);
      }
    }

    if (Object.keys(properties).length) {
      cartItem.properties = properties;
    }

    const sellingPlan = String(
      formData.get('selling_plan') || ''
    );

    if (/^\d+$/.test(sellingPlan)) {
      cartItem.selling_plan = Number(sellingPlan);
    }

    if (submitButton) {
      submitButton.disabled = true;
    }

    productComponent.setAttribute(
      'aria-busy',
      'true'
    );

    showStatus(
      addonRoot,
      'Adding your products...',
      false
    );

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [cartItem, ...addOnItems]
          })
        }
      );

      const body = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || body.status) {
        throw new Error(
          body.description ||
          body.message ||
          'Could not add your products.'
        );
      }

      window.location.assign(`${root}cart`);

    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';

      showStatus(addonRoot, message, true);

      if (submitButton) {
        submitButton.disabled = false;
      }

      productComponent.removeAttribute(
        'aria-busy'
      );

      busyForms.delete(form);
    }
  }

  function onPreorderSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const productComponent = form.closest(
      'product-form-component[data-redirect-to-checkout="true"]'
    );

    if (!productComponent) return;

    const productGrid = productComponent.closest(
      'product-component'
    );

    const addonRoot =
      productGrid?.querySelector(addonSelector);

    if (!addonRoot) return;

    let addOnItems;

    try {
      addOnItems = selectedAddons(addonRoot);
    } catch (error) {
      event.preventDefault();
      event.stopImmediatePropagation();

      showStatus(
        addonRoot,
        error.message,
        true
      );

      return;
    }

    // Preserve native Horizon submission with no add-ons.
    if (!addOnItems.length) return;

    // With selected add-ons, add every line in one request.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!form.reportValidity()) return;

    const button = form.querySelector(
      'button[type="submit"][name="add"]'
    );

    if (button?.disabled || busyForms.has(form)) {
      return;
    }

    const quantitySelector =
      productComponent.refs?.quantitySelector;

    const validation =
      quantitySelector?.canAddToCart?.();

    if (validation && !validation.canAdd) {
      showStatus(
        addonRoot,
        'Please check the quantity of the main product.',
        true
      );

      return;
    }

    void submitWithAddons(
      form,
      productComponent,
      addonRoot,
      addOnItems
    );
  }

  /* -----------------------------------------
     PAGE AND THEME EDITOR LIFECYCLE
     ----------------------------------------- */

  function init() {
    initDetails();
    initAddons();
  }

  function onSectionUnload(event) {
    for (
      const element of Array.from(
        detailsInstances.keys()
      )
    ) {
      if (event.target.contains(element)) {
        disposeDetails(element);
      }
    }
  }

  document.addEventListener(
    'click',
    onQuantityClick
  );

  document.addEventListener(
    'change',
    onQuantityChange
  );

  window.addEventListener(
    'submit',
    onPreorderSubmit,
    true
  );

  document.addEventListener(
    'shopify:section:load',
    init
  );

  document.addEventListener(
    'shopify:section:unload',
    onSectionUnload
  );

  window.addEventListener(
    'wambolt:page:before-leave',
    disposeAllDetails
  );

  window.addEventListener(
    'wambolt:page:enter',
    init
  );

  window.addEventListener(
    'resize',
    init,
    { passive: true }
  );

  window[registryKey] = { init };

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    document
      .querySelectorAll('product-form-component[data-redirect-to-checkout="true"][aria-busy="true"]')
      .forEach((component) => {
        component.removeAttribute('aria-busy');

        const form = component.querySelector('form');
        if (form) busyForms.delete(form);

        const button = component.querySelector('button[type="submit"][name="add"]');
        if (button) button.disabled = false;
      });

    document
      .querySelectorAll('[data-wambolt-addon-status]')
      .forEach((status) => {
        status.hidden = true;
        status.textContent = '';
      });
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();
